import { useEffect, useMemo, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  RotateCcw,
  Sparkles,
  X,
} from 'lucide-react';
import type { AgenticTask, TaskStep } from '@jam/shared';
import { useAuth } from '@/components/providers/auth-provider';
import { useUiStore } from '@/stores/ui-store';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';
import {
  dismissTask,
  retryTask,
  runDemoTask,
  runDossierTask,
  subscribeRecentTasks,
} from '@/lib/tasks';

const RECENT_WINDOW_MS = 30_000;

/**
 * Persistent bottom-end task tray (frontend spec §5.8).
 *
 * Subscribes to the current analyst's tasks and surfaces queued / running
 * tasks plus tasks that completed within the last 30 seconds. Hidden when
 * there's nothing to show.
 */
export function TaskTray() {
  const { user, activeOrg } = useAuth();
  const isAnalyst = Boolean(activeOrg);
  const lang = useUiStore((s) => s.lang);
  const [tasks, setTasks] = useState<AgenticTask[]>([]);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [demoBusy, setDemoBusy] = useState(false);

  // Detect the active dossier entity (if any) from the URL so the dev
  // button can prefer the real server-driven task over the simulator.
  const activeDossierEntity = useRouterState({
    select: (s) => {
      const path = s.location.pathname;
      const match = /^\/entity\/([^/]+)/.exec(path);
      if (!match) return null;
      try {
        return decodeURIComponent(match[1]!);
      } catch {
        return match[1]!;
      }
    },
  });

  useEffect(() => {
    if (!user || !isAnalyst) return undefined;
    return subscribeRecentTasks(user.uid, setTasks);
  }, [user, isAnalyst]);

  // Tick every 5s so recently-completed tasks fall off the tray on time.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, []);

  const visible = useMemo(
    () =>
      tasks.filter((task) => {
        if (task.status === 'queued' || task.status === 'running') return true;
        const updated = new Date(task.updatedAt).getTime();
        return now - updated < RECENT_WINDOW_MS;
      }),
    [tasks, now],
  );

  if (!user || !isAnalyst) return null;
  if (visible.length === 0) return null;

  const active = visible.filter(
    (t) => t.status === 'queued' || t.status === 'running',
  );
  const headlineTask = active.length > 0 ? active[0] : visible[0];
  if (!headlineTask) return null;
  const headline = summarize(headlineTask, lang);

  return (
    <div
      className="fixed bottom-4 end-4 z-40 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col"
      role="region"
      aria-label={t('task.tray.title', lang)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center justify-between gap-3 rounded-sm border border-[var(--color-border)] bg-[var(--color-elevated)] px-3 py-2 text-start text-xs text-[var(--color-ink)] shadow-md transition-colors hover:bg-[var(--color-accent-softer)]',
        )}
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          {active.length > 0 ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin text-[var(--color-accent)]" />
          ) : (
            <Sparkles className="size-3.5 shrink-0 text-[var(--color-accent)]" />
          )}
          <span className="truncate">
            {active.length > 0
              ? `${active.length} active · ${headline}`
              : headline}
          </span>
        </span>
        {open ? (
          <ChevronDown className="size-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronUp className="size-3.5 shrink-0" aria-hidden />
        )}
      </button>

      {open && (
        <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] p-2 shadow-lg">
          <div className="mb-2 flex items-center justify-between gap-2 px-1">
            <span className="text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
              {t('task.tray.title', lang)}
            </span>
            {import.meta.env.DEV && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={demoBusy}
                onClick={async () => {
                  setDemoBusy(true);
                  try {
                    if (activeDossierEntity) {
                      await runDossierTask(activeDossierEntity);
                    } else {
                      await runDemoTask('Trita Parsi');
                    }
                  } finally {
                    setDemoBusy(false);
                  }
                }}
              >
                {t('task.demo', lang)}
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {visible.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function summarize(task: AgenticTask, lang: 'en' | 'fa'): string {
  if (lang === 'fa') {
    return task.title;
  }
  return task.title;
}

function TaskCard({ task }: { task: AgenticTask }) {
  const lang = useUiStore((s) => s.lang);
  const total = task.steps.length || 1;
  const done = task.steps.filter((s) => s.status === 'done').length;
  const failed = task.steps.some((s) => s.status === 'failed');
  const pct = Math.round((done / total) * 100);

  const isEntityDeliverable =
    task.finalDeliverable?.startsWith('/entity/') ?? false;

  return (
    <div className="rounded-sm border border-[var(--color-border)] bg-[var(--color-elevated)] p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-[var(--color-ink)]">
            {task.title}
          </div>
          {task.entityName && (
            <span className="mt-1 inline-block rounded-xs bg-[var(--color-accent-soft)] px-1.5 py-0.5 text-xxs uppercase tracking-wider text-[var(--color-accent)]">
              {task.entityName}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => dismissTask(task.id)}
          aria-label="Dismiss task"
          className="shrink-0 rounded-xs p-0.5 text-[var(--color-ink-muted)] hover:bg-[var(--color-accent-softer)] hover:text-[var(--color-ink)]"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>

      <ul className="mb-2 space-y-1">
        {task.steps.map((step, i) => (
          <li
            key={`${task.id}-${i}`}
            className="flex items-start gap-2 text-xs text-[var(--color-ink-secondary)]"
          >
            <StepIcon step={step} />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{step.label}</span>
              {step.detail && (
                <span className="block truncate text-xxs italic text-[var(--color-ink-muted)]">
                  {step.detail}
                </span>
              )}
              {step.result && step.status === 'done' && (
                <span className="block truncate text-xxs text-[var(--color-ink-muted)]">
                  {step.result}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div
        className="h-1 w-full overflow-hidden rounded-full bg-[var(--color-border)]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div
          className={cn(
            'h-full transition-[width] duration-280',
            failed ? 'bg-[var(--color-critical)]' : 'bg-[var(--color-accent)]',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {task.status === 'done' && task.finalDeliverable && (
        <div className="mt-2 text-end">
          {isEntityDeliverable ? (
            <Link
              to="/entity/$name"
              params={{
                name: decodeURIComponent(
                  task.finalDeliverable.replace('/entity/', ''),
                ),
              }}
              className="text-xs text-[var(--color-accent)] hover:underline"
            >
              {t('task.open', lang)} →
            </Link>
          ) : (
            <a
              href={task.finalDeliverable}
              className="text-xs text-[var(--color-accent)] hover:underline"
            >
              {t('task.open', lang)} →
            </a>
          )}
        </div>
      )}

      {task.status === 'failed' && task.entityName && (
        <RetryButton task={task} />
      )}
    </div>
  );
}

function RetryButton({ task }: { task: AgenticTask }) {
  const lang = useUiStore((s) => s.lang);
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-2 flex items-center justify-end">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await retryTask(task);
          } finally {
            setBusy(false);
          }
        }}
        className="gap-1.5 text-xs"
      >
        <RotateCcw className="size-3.5" aria-hidden />
        {t('task.retry', lang)}
      </Button>
    </div>
  );
}

function StepIcon({ step }: { step: TaskStep }) {
  switch (step.status) {
    case 'pending':
      return (
        <Clock
          className="mt-0.5 size-3.5 shrink-0 text-[var(--color-ink-muted)]"
          aria-label="pending"
        />
      );
    case 'running':
      return (
        <Loader2
          className="mt-0.5 size-3.5 shrink-0 animate-spin text-[var(--color-accent)]"
          aria-label="running"
        />
      );
    case 'done':
      return (
        <Check
          className="mt-0.5 size-3.5 shrink-0 text-[var(--color-accent)]"
          aria-label="done"
        />
      );
    case 'failed':
      return (
        <X
          className="mt-0.5 size-3.5 shrink-0 text-[var(--color-critical)]"
          aria-label="failed"
        />
      );
    default:
      return null;
  }
}
